/**
 * Contrast maths — WCAG 2.1 relative luminance and the two "move it until it passes" helpers.
 *
 * Ported from the mobile app's `src/design-system/contrast.ts`. A trainer-chosen brand colour is not
 * knowable ahead of time, so the tuning the ember accent got by hand has to happen at runtime:
 * nothing is refused on contrast, the fill or the ink is ADJUSTED until the screen reads.
 *
 * Two readers: `/konto`'s phone preview, which is a light screen in both themes and only ever
 * darkens, and `entities/brand-color`'s token derivation, which paints the trainer's own app in
 * both web themes and therefore lightens its inks on Flame Dark. The second one also needs
 * `compositeOver`, because an accent wash is translucent and a ratio is only defined between two
 * opaque colours. Promoted here from `pages/account/model` on 23 September 2026, on that second
 * reader.
 */

import { cylinderToRgb, parseHex, toHex, type Rgb } from './hex';

/** WCAG AA floor for text under 18.66px/700 — the one the CTA fill holds. */
export const AA_TEXT_RATIO = 4.5;
/** Which way a hue may travel to gain contrast. */
export type ContrastDirection = 'darken' | 'lighten';

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(color: string): number {
  const { r, g, b } = parseHex(color);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/**
 * A translucent `color` (channels 0–255, `alpha` 0–1) flattened onto an opaque `backdrop` — what the
 * eye actually sees, and therefore what a contrast ratio has to be measured against.
 */
export function compositeOver(color: string, alpha: number, backdrop: string): string {
  const fg = parseHex(color);
  const bg = parseHex(backdrop);
  return toHex({
    r: fg.r * alpha + bg.r * (1 - alpha),
    g: fg.g * alpha + bg.g * (1 - alpha),
    b: fg.b * alpha + bg.b * (1 - alpha),
  });
}

/** WCAG contrast ratio between two OPAQUE colours, 1–21. Order does not matter. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h =
    max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  return toHex(cylinderToRgb(h, c, l - c / 2));
}

/**
 * `color` moved the way `reference` was moved to reach `shaded`: the same hue, its saturation and
 * lightness scaled by the ratios between those two. How a brand colour gets the shade a hand-picked
 * Flame value holds against the ember, without a table per colour.
 */
export function shadeLike(color: string, reference: string, shaded: string): string {
  const base = rgbToHsl(parseHex(color));
  const from = rgbToHsl(parseHex(reference));
  const to = rgbToHsl(parseHex(shaded));
  const ratio = (a: number, b: number) => (b === 0 ? 1 : a / b);
  return hslToHex({
    h: base.h,
    s: Math.min(Math.max(base.s * ratio(to.s, from.s), 0), 1),
    l: Math.min(Math.max(base.l * ratio(to.l, from.l), 0), 1),
  });
}

/** Lightness step per iteration — 1pp keeps the result within one perceptual step of the threshold. */
const STEP = 0.01;

/**
 * Walk `color` along lightness in `direction` until `passes` accepts it, and return the FIRST colour
 * that does — or `null` when this direction runs out of room.
 *
 * The `null` is what the two callers below differ on, and it is why this is a private helper rather
 * than the exported thing: an INK that cannot clear the floor still has to be legible, so it falls
 * back to the extreme, while a FILL has a second direction to try first.
 */
function adjustUntil(
  color: string,
  direction: ContrastDirection,
  passes: (candidate: string) => boolean,
): string | null {
  const step = direction === 'darken' ? -STEP : STEP;
  const start = rgbToHsl(parseHex(color));
  for (let i = 0; i <= 100; i += 1) {
    const l = start.l + step * i;
    if (l < 0 || l > 1) break;
    const candidate = hslToHex({ ...start, l });
    if (passes(candidate)) return candidate;
  }
  return null;
}

/**
 * The readable INK of a hue: `color` moved until it clears `minRatio` against EVERY surface it may
 * be drawn on.
 *
 * Pass all of them. The darkest surface on a light screen is what binds, and measuring only one is
 * how an ink passes on the card and fails on the page under it.
 */
export function readableInk(
  color: string,
  surfaces: readonly string[],
  minRatio: number,
  direction: ContrastDirection,
): string {
  // The surfaces' luminances are fixed for the whole walk; only the candidate's changes.
  const grounds = surfaces.map(relativeLuminance);
  const passes = (candidate: string) => {
    const lum = relativeLuminance(candidate);
    return grounds.every(
      (ground) => (Math.max(lum, ground) + 0.05) / (Math.min(lum, ground) + 0.05) >= minRatio,
    );
  };
  // A hue that cannot clear the floor at any lightness must still return something legible, never
  // the failing input.
  return adjustUntil(color, direction, passes) ?? (direction === 'darken' ? '#000000' : '#ffffff');
}

/**
 * The readable FILL for a fixed ink — the counterpart of `readableInk`, moving the surface instead
 * of the words. This is what the preview's call to action does with a custom brand colour: a bright
 * hue lands on a deeper fill, which is the price of keeping a white label readable.
 *
 * ── It tries BOTH directions, and the mobile port does not ───────────────────────────────────
 * The phone picks one direction off the ink (`lum(ink) > lum(color) ? darken : lighten`) and lives
 * with it. Measured on the whole hue wheel, that leaves exactly one hole and it is a real one: a
 * trainer who picks **#ffffff** gets `lighten` — white is not brighter than white — which walks
 * straight into the ceiling and hands back the failing input, a white label on a white button at
 * 1:1. The preferred direction is kept (it keeps the fill nearest the colour that was picked), and
 * when it runs out of room the other one is tried, which for a white fill is a deep grey that reads.
 * Worth raising upstream; recorded here rather than silently diverging.
 */
export function readableFill(color: string, ink: string, minRatio: number): string {
  const passes = (candidate: string) => contrastRatio(ink, candidate) >= minRatio;
  const preferred: ContrastDirection =
    relativeLuminance(ink) > relativeLuminance(color) ? 'darken' : 'lighten';
  const other: ContrastDirection = preferred === 'darken' ? 'lighten' : 'darken';
  return (
    adjustUntil(color, preferred, passes) ??
    adjustUntil(color, other, passes) ??
    // Neither direction reaches the floor — a ratio nothing can hold against this ink. Hand back
    // the extreme furthest from it, so the label is as readable as the colour space allows.
    (relativeLuminance(ink) > 0.5 ? '#000000' : '#ffffff')
  );
}
